import type {
  CellKeyDownEvent,
  DefaultMenuItem,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  IsGroupOpenByDefaultParams,
  MenuItemDef,
  ModelUpdatedEvent,
  RowGroupOpenedEvent,
  SelectionChangedEvent,
  Theme
} from 'ag-grid-community';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { MemberRow, MemberSubsetOp } from 'src/components/star-grid/utils/subset-editor/memberSubset';
import {
  buildSubsetContextMenu,
  subsetAutoGroupColumnDef,
  subsetColumnDefs,
  subsetTheme
} from 'src/components/star-grid/utils/subset-editor/memberSubsetGridConfig';
import { reconcileMemberSelection } from 'src/components/star-grid/utils/subset-editor/memberSubsetSelection';

export interface MemberSubsetGridOptions {
  readonly rows: readonly MemberRow[];
  readonly allRows: readonly MemberRow[];
  readonly multiple: boolean;
  readonly cascade: boolean;
  readonly theme: Theme;
  readonly checked: ReadonlySet<string>;
  readonly onCheckedChanged: (keys: readonly string[]) => void;
  readonly onExpandedChanged: (key: string, expanded: boolean) => void;
  readonly initiallyExpanded: ReadonlySet<string>;
  readonly onRunOp: (op: MemberSubsetOp, selected: readonly string[]) => void;
  readonly onRemove: (uids: readonly string[], withDescendants: boolean) => void;
  readonly onAddParents: (clickedKey: string) => void;
  readonly onDrill: (uid: string, key: string) => void;
  readonly onApiReady?: (api: GridApi<MemberRow>) => void;
}

/**
 * The subset grid's AG Grid options and event handlers.
 *
 * Everything is memoized: these are grid OPTIONS, so a fresh identity is an option change AG Grid
 * reconciles on every render — re-applying the theme or recomputing master state — which made rows flicker.
 */
export const useMemberSubsetGrid = ({
  rows,
  allRows,
  multiple,
  cascade,
  theme,
  checked,
  onCheckedChanged,
  onExpandedChanged,
  initiallyExpanded,
  onRunOp,
  onRemove,
  onAddParents,
  onDrill,
  onApiReady
}: MemberSubsetGridOptions) => {
  // The context menu renders inside the grid's own container, the only place stylesheet rules can reach it.
  const [popupParent, setPopupParent] = useState<HTMLDivElement | null>(null);

  const gridTheme = useMemo(() => subsetTheme(theme), [theme]);
  const columnDefs = useMemo(() => subsetColumnDefs(), []);
  const autoGroupColumnDef = useMemo(() => subsetAutoGroupColumnDef(), []);

  // The one supported way to make a childless row expandable; the detail row it would open is suppressed.
  const isRowMaster = useCallback((data: MemberRow): boolean => data.canDrill === true, []);
  const detailCellRenderer = useCallback(() => null, []);

  // The path is a chain of row uids, so multi-parent copies stay separate rows and no placeholder groups appear.
  const getDataPath = useCallback((row: MemberRow): string[] => [...row.displayPath], []);
  // The uid, not the position key: one member can appear twice, and duplicate ids make AG Grid drop rows.
  const getRowId = useCallback(({ data }: { data: MemberRow }): string => data.uid, []);

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<MemberRow>): (MenuItemDef | DefaultMenuItem)[] =>
      buildSubsetContextMenu(params.node?.data, checked, params.api, { onRunOp, onRemove, onAddParents }),
    [checked, onRunOp, onRemove, onAddParents]
  );

  const onRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent<MemberRow>) => {
      const data = event.data;
      // A `Parent` copy shares its position key with the original, so mirroring it would reopen the original.
      if (!data || data.added === true) return;
      // A row with no children in the set: opening it means "show me the rollup", so it drills instead.
      if (data.canDrill === true && event.node.expanded === true) {
        onDrill(data.uid, data.key);
        return;
      }
      onExpandedChanged(data.key, event.node.expanded === true);
    },
    [onExpandedChanged, onDrill]
  );

  // Seeds the grid's expansion once per mount; never pushed back afterwards, which is how loops start.
  const isGroupOpenByDefault = useCallback(
    (params: IsGroupOpenByDefaultParams<MemberRow>): boolean => initiallyExpanded.has(params.rowNode.data?.uid ?? ''),
    [initiallyExpanded]
  );

  /*
   * Ticks each row once, the first time that ROW is seen: a drill splices rows in without remounting, so a
   * mount-time pass left them unticked. Only ever adds, so a key the user unticked is never re-selected.
   */
  const seenRef = useRef<Set<string>>(new Set());
  const seedSelection = useCallback(
    (api: GridApi<MemberRow>) => {
      const nodes: IRowNode<MemberRow>[] = [];
      api.forEachNode((node) => {
        const data = node.data;
        if (!data || seenRef.current.has(data.uid)) return;
        seenRef.current.add(data.uid);
        if (checked.has(data.key)) nodes.push(node);
      });
      if (nodes.length > 0) api.setNodesSelected({ nodes, newValue: true });
    },
    [checked]
  );

  // No loop: the keys this selects are already checked, so the resulting event reports an equal set.
  const onModelUpdated = useCallback((event: ModelUpdatedEvent<MemberRow>) => seedSelection(event.api), [seedSelection]);

  /*
   * Shift + Arrow Up/Down grows AND shrinks the row selection. AG Grid binds shift to row selection for
   * clicks but never for arrow keys, so it is written here. The range is always exactly [anchor, extent],
   * held as row uids because expanding or collapsing renumbers the displayed rows. Rollups are selected
   * like any other row, which is what makes "select several consolidations, then Keep + Leaves" one gesture.
   * Do NOT enable `cellSelection`: shift+arrow would stop moving focus and this would stop extending.
   */
  const rangeRef = useRef<{ anchorUid: string; extentUid: string } | null>(null);

  const nodesBetween = useCallback((api: GridApi<MemberRow>, from: number, to: number): IRowNode<MemberRow>[] => {
    const nodes: IRowNode<MemberRow>[] = [];
    for (let index = Math.min(from, to); index <= Math.max(from, to); index += 1) {
      const node = api.getDisplayedRowAtIndex(index);
      if (node) nodes.push(node);
    }
    return nodes;
  }, []);

  // Null once the row is collapsed out of view, which re-anchors.
  const displayedIndexOf = useCallback((api: GridApi<MemberRow>, uid: string): number | null => {
    return api.getRowNode(uid)?.rowIndex ?? null;
  }, []);

  const onCellKeyDown = useCallback(
    (event: CellKeyDownEvent<MemberRow>) => {
      if (!multiple) return;
      const keyboardEvent = event.event as KeyboardEvent | null;
      if (!keyboardEvent) return;
      const down = keyboardEvent.key === 'ArrowDown';
      const up = keyboardEvent.key === 'ArrowUp';
      if (!down && !up) return;

      const focused = event.node.rowIndex;
      const focusedUid = event.node.data?.uid;
      if (focused === null || !focusedUid) return;

      if (!keyboardEvent.shiftKey) {
        // Plain navigation re-anchors, so the next shift-arrow measures from where you actually are.
        rangeRef.current = null;
        return;
      }

      // Resumed only while focus is still where the range left it; anything else re-anchors here.
      const stored = rangeRef.current;
      const resumable = stored !== null && stored.extentUid === focusedUid ? stored : null;
      const anchorIndex = resumable ? displayedIndexOf(event.api, resumable.anchorUid) : null;
      const extentIndex = resumable ? displayedIndexOf(event.api, resumable.extentUid) : null;
      const previous =
        anchorIndex === null || extentIndex === null ? { anchor: focused, extent: focused } : { anchor: anchorIndex, extent: extentIndex };

      // AG Grid moves focus itself on arrow keys, so the range runs to where focus is heading.
      const extent = focused + (down ? 1 : -1);
      const last = event.api.getDisplayedRowCount() - 1;
      if (extent < 0 || extent > last) return;

      const nodes = nodesBetween(event.api, previous.anchor, extent);
      const inNewRange = new Set(nodes.map((node) => node.id));
      const retreated = nodesBetween(event.api, previous.anchor, previous.extent).filter((node) => !inNewRange.has(node.id));

      if (retreated.length > 0) event.api.setNodesSelected({ nodes: retreated, newValue: false });
      if (nodes.length > 0) event.api.setNodesSelected({ nodes, newValue: true });

      const anchorUid = event.api.getDisplayedRowAtIndex(previous.anchor)?.data?.uid;
      const extentUid = event.api.getDisplayedRowAtIndex(extent)?.data?.uid;
      rangeRef.current = anchorUid && extentUid ? { anchorUid, extentUid } : null;
    },
    [nodesBetween, displayedIndexOf, multiple]
  );

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<MemberRow>) => {
      seedSelection(event.api);
      // Published here rather than on grid-ready, so the caller only gets an API that has rows in it.
      onApiReady?.(event.api);
    },
    [seedSelection, onApiReady]
  );

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<MemberRow>) => {
      if (event.source === 'rowDataChanged' || event.source === 'gridInitializing') return;
      // `isSelected()`, not `getSelectedNodes()`: under cascading selection the latter omits group rows.
      const keys: string[] = [];
      event.api.forEachNode((node) => {
        if (node.data && node.isSelected() === true) keys.push(node.data.key);
      });
      onCheckedChanged(multiple && cascade ? reconcileMemberSelection(checked, rows, allRows, keys, event.source === 'apiSelectAll') : keys);
    },
    [onCheckedChanged, multiple, cascade, checked, rows, allRows]
  );

  return {
    popupParent,
    setPopupParent,
    gridTheme,
    columnDefs,
    autoGroupColumnDef,
    isRowMaster,
    detailCellRenderer,
    getDataPath,
    getRowId,
    getContextMenuItems,
    onRowGroupOpened,
    isGroupOpenByDefault,
    onModelUpdated,
    onCellKeyDown,
    onFirstDataRendered,
    onSelectionChanged
  };
};
